import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate, LOCALE_MAP } from 'utils/locale-formatting';
import { MaturityBadge } from '../../Shared';
import BusinessIcon from '@mui/icons-material/Business';
import SecurityIcon from '@mui/icons-material/Security';
import PolicyIcon from '@mui/icons-material/Policy';

/**
 * OverviewTab - Overview summary for protected view
 * @param {object} organization - Organization details
 * @param {Array} frameworks - Framework list
 * @param {object} governanceStructure - Governance structure
 */
export const OverviewTab = ({ organization, frameworks = [], governanceStructure }) => {
  const { t, i18n } = useTranslation('transparency');
  const locale = LOCALE_MAP[i18n.language] ?? 'en-US';
  const frameworkCount = frameworks?.length || 0;
  const categoryCount = governanceStructure?.categories?.length || 0;

  return (
    <Box>
      {/* Organization Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: '#1976d2' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {organization?.name}
            </Typography>
            {organization?.description && (
              <Typography variant="body2" color="text.secondary">
                {organization.description}
              </Typography>
            )}
          </Box>
        </Box>
        {organization?.lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            {t('protected.overview.lastUpdated', { date: formatDate(organization.lastUpdated, locale) })}
          </Typography>
        )}
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <SecurityIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} color="primary">
              {frameworkCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('protected.overview.frameworks')}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <PolicyIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />
            <Typography variant="h4" fontWeight={700} sx={{ color: '#4caf50' }}>
              {categoryCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('protected.overview.governanceCategories')}
            </Typography>
          </Paper>
        </Grid>

        {governanceStructure?.overallPosture && (
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Box sx={{ mb: 1 }}>
                <MaturityBadge label={governanceStructure.overallPosture.maturityLabel} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('protected.overview.overallMaturity')}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Quick Framework List */}
      {frameworks && frameworks.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            {t('protected.overview.frameworksInUse')}
          </Typography>
          <Grid container spacing={2}>
            {frameworks.slice(0, 6).map((fw, index) => (
              <Grid item xs={12} sm={6} md={4} key={fw.frameworkId || index}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle2">{fw.name}</Typography>
                  <MaturityBadge label={fw.maturityLabel} size="small" />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};
